<template>
    <ValidationObserver ref="observer" v-slot="{ invalid }">
        <BaseModalFront
            :show="true"
            :title="$t('perfil.familia.add_modal_titulo')"
            :confirm-text="preview ? $t('perfil.familia.add_modal_vincular') : $t('perfil.familia.add_modal_validar')"
            :confirm-disabled="invalid || loadingValidar || loadingAgregar"
            :loading="loadingValidar || loadingAgregar"
            @close="emit('close')"
            @confirm="preview ? onAgregar() : onValidar()"
        >
            <div class="tw:flex tw:flex-col tw:gap-4">
                <InputText
                    id="familia-numero-abonado"
                    :value="numeroAbonado"
                    :label="$t('perfil.familia.add_modal_numero_label')"
                    name="numero_abonado"
                    rules="required"
                    :disabled="loadingValidar || loadingAgregar"
                    @input="onInputNumero"
                />

                <InputText
                    id="familia-dni"
                    :value="dni"
                    :label="$t('perfil.familia.add_modal_dni_label')"
                    name="dni"
                    rules="required"
                    :disabled="loadingValidar || loadingAgregar"
                    @input="onInputDni"
                />

                <!-- Preview del abonado -->
                <div
                    v-if="preview"
                    class="tw:flex tw:items-center tw:gap-3 tw:rounded-lg tw:border tw:border-green-200 tw:bg-green-50 tw:p-3"
                >
                    <i class="material-icons tw:text-green-600">check_circle</i>
                    <div>
                        <p class="tw:text-sm tw:font-semibold tw:text-green-800">
                            {{ preview.name }} {{ preview.surnames }}
                        </p>
                        <p class="tw:text-xs tw:text-green-600">
                            {{ $t('perfil.familia.add_modal_n_abonado', { numero: numeroAbonado }) }}
                        </p>
                    </div>
                </div>

                <!-- Error genérico -->
                <div
                    v-if="errorGeneral"
                    class="tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50 tw:p-3 tw:text-sm tw:text-red-700"
                >
                    {{ errorGeneral }}
                </div>
            </div>
        </BaseModalFront>
    </ValidationObserver>
</template>

<script setup>
    import { ref } from 'vue';
    import BaseModalFront from '@/modules/front/ui/BaseModalFront.vue';
    import InputText from '@/modules/front/ui/InputText.vue';
    import { t } from '@/utils/i18n-helper';

    const props = defineProps({
        validarMiembro: {
            type: Function,
            required: true,
        },
        agregarMiembro: {
            type: Function,
            required: true,
        },
    });

    const emit = defineEmits(['close', 'added']);

    const observer = ref(null);
    const numeroAbonado = ref('');
    const dni = ref('');
    const preview = ref(null);
    const loadingValidar = ref(false);
    const loadingAgregar = ref(false);
    const errorGeneral = ref(null);

    const onInputNumero = val => {
        numeroAbonado.value = val;
        observer.value?.validate();
    };
    const onInputDni = val => {
        dni.value = val;
        observer.value?.validate();
    };

    const onValidar = async() => {
        const isValid = await observer.value?.validate();
        if (!isValid) return;

        loadingValidar.value = true;
        errorGeneral.value = null;
        preview.value = null;
        try {
            const result = await props.validarMiembro(numeroAbonado.value.trim(), dni.value.trim());
            if (result) {
                preview.value = result;
            } else {
                errorGeneral.value = t('perfil.familia.add_modal_error_no_encontrado', {}, 'No se encontró ningún abonado con esos datos.');
            }
        } catch (err) {
            const status = err?.response?.status;
            if (status === 422) {
                const data = err?.response?.data;
                if (data?.errors) {
                    // Inyecta errores en VeeValidate para que los inputs se pongan rojos
                    observer.value?.setErrors(data.errors);
                } else {
                    errorGeneral.value = data?.message ?? t('perfil.familia.add_modal_error_datos', {}, 'Datos no válidos. Revisa el número de abonado y el DNI.');
                }
            } else if (status === 403) {
                errorGeneral.value = t('perfil.familia.add_modal_error_feature', {}, 'La funcionalidad de familias no está disponible para este teatro.');
            } else {
                errorGeneral.value = err?.message ?? t('perfil.familia.add_modal_error_validar', {}, 'Error al validar el abonado.');
            }
        } finally {
            loadingValidar.value = false;
        }
    };

    const onAgregar = async() => {
        loadingAgregar.value = true;
        errorGeneral.value = null;
        try {
            await props.agregarMiembro(numeroAbonado.value.trim(), dni.value.trim());
            emit('added');
            emit('close');
        } catch (err) {
            const status = err?.response?.status;
            if (status === 422) {
                const data = err?.response?.data;
                if (data?.errors) {
                    observer.value?.setErrors(data.errors);
                } else {
                    errorGeneral.value = data?.message ?? t('perfil.familia.add_modal_error_vincular', {}, 'No se pudo vincular al abonado. Verifica los datos.');
                }
            } else {
                errorGeneral.value = err?.message ?? t('perfil.familia.add_modal_error_vincular_generico', {}, 'Error al vincular al abonado.');
            }
        } finally {
            loadingAgregar.value = false;
        }
    };
</script>
